import { Product as ProductSchemaType, ImageObject as ImageSchemaType } from 'schema-dts';
import { CatalogProduct } from '@interfaces/models/catalogProduct';
import { ContentsPageMetadata } from '@interfaces/models/contentsPageMetadata';
import { ElasticSearchProduct } from '@interfaces/api/responses/elasticSearch';
import { Product } from '@interfaces/models/product';
import { User } from '@interfaces/models/user';
import UserBadge from '@enums/userBadge';
import { ImagesService } from '@services/image-service';
import { getGoogleShoppingCondition } from './schema-utils';

type ProductSchemaCommonFields = {
  name?: string;
  path?: string;
  origin?: string;
  image?: ProductSchemaType['image'];
  brandName?: string;
  priceCents?: number;
  priceCurrency?: string;
  regularPriceCents?: number;
  conditionId?: string | number;
  isUnavailable?: boolean;
};

type ProductSchemaSource = {
  name?: string;
  path?: string;
  image?: ProductSchemaType['image'];
  brand?: {
    name?: string;
  };
  price?: {
    cents?: number;
    currency?: string;
  };
  regularPrice?: {
    cents?: number;
  };
  condition?: {
    id?: string | number;
  };
  sold?: boolean;
  shouldBeGone?: boolean;
};

type ProductSchemaOptions = {
  includeContext?: boolean;
  productFields?: Record<string, unknown>;
  offerFields?: Record<string, unknown>;
};

type ProductSchemaOverrides = {
  origin?: string;
  image?: ProductSchemaCommonFields['image'];
  conditionId?: string | number;
  isUnavailable?: boolean;
};

const SIZE_SYSTEM_SCHEMA_URL_BY_STANDARD: Record<string, string> = {
  DE: 'https://schema.org/WearableSizeSystemDE',
  EU: 'https://schema.org/WearableSizeSystemEurope',
  FR: 'https://schema.org/WearableSizeSystemFR',
  IT: 'https://schema.org/WearableSizeSystemIT',
  UK: 'https://schema.org/WearableSizeSystemUK',
  US: 'https://schema.org/WearableSizeSystemUS',
};

const MEASUREMENTS_TO_INCLUDE = ['Height', 'Width', 'Depth'];

const getProductUrl = (origin?: string, path?: string): string | undefined => {
  if (!path) {
    return undefined;
  }

  return origin ? `${origin}${path}` : path;
};

// Snap to start of UTC day so SSR and client hydration produce the same date.
const getPriceValidUntil = (): string => {
  const date = new Date();
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCDate(date.getUTCDate() + 60);
  return date.toISOString().split('T')[0];
};

const getPriceSpecification = (
  regularCents: ProductSchemaCommonFields['regularPriceCents'],
  currentCents: ProductSchemaCommonFields['priceCents'],
  priceCurrency: ProductSchemaCommonFields['priceCurrency'],
) => {
  if (!regularCents || !currentCents || regularCents <= currentCents) {
    return {};
  }

  return {
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      priceType: 'https://schema.org/StrikethroughPrice',
      price: (regularCents / 100).toString(),
      priceCurrency: priceCurrency ?? '',
    },
  };
};

const getCommonProductFields = (
  product: ProductSchemaSource,
  overrides: ProductSchemaOverrides = {},
): ProductSchemaCommonFields => ({
  name: product.name,
  path: product.path,
  origin: overrides.origin,
  image: overrides.image ?? product.image,
  brandName: product.brand?.name,
  priceCents: product.price?.cents,
  priceCurrency: product.price?.currency,
  regularPriceCents: product.regularPrice?.cents,
  conditionId: overrides.conditionId ?? product.condition?.id,
  isUnavailable: overrides.isUnavailable ?? Boolean(product.sold || product.shouldBeGone),
});

const getSeller = (seller: User, origin: string) => {
  const sellerUrl = origin && seller?.id ? `${origin}/profile/${seller.id}` : undefined;
  return {
    '@type': seller?.badges?.includes('pro' as UserBadge) ? 'Organization' : 'Person',
    name: seller?.firstname,
    ...(sellerUrl && { '@id': sellerUrl, url: sellerUrl }),
  };
};

const getOptionalMeasurements = (product: Product): Record<string, unknown> => {
  return Object.fromEntries(
    MEASUREMENTS_TO_INCLUDE.map((optionalMeasurement) => {
      const productMeasurement = product?.measurements?.find((measurement) => measurement.name === optionalMeasurement);
      if (!productMeasurement) {
        return [null, null];
      }
      return [
        [optionalMeasurement],
        {
          '@type': 'QuantitativeValue',
          value: productMeasurement.value,
          ...(!!product?.unit && {
            unitCode: product?.unit,
          }),
        },
      ];
    }).filter((item) => item[0] !== null),
  ) as Record<string, unknown>;
};

const createImageSchema = (product: Product): ImageSchemaType => ({
  '@type': 'ImageObject',
  contentUrl: ImagesService.buildUrl(product?.pictures?.[0]?.path, { w: 1024, h: 1024 }, true),
  description: product?.name,
  width: '1024',
  height: '1024',
});

const getDescription = (description: string, seoDesc: string) =>
  description?.length < 50 ? seoDesc || description : description;

const getAdditionalProperties = (product: Product): Record<string, unknown> => {
  const properties: Array<{ '@type': string; name: string; value: string }> = [];

  if (product?.condition?.description) {
    properties.push({
      '@type': 'PropertyValue',
      name: 'conditionGrade',
      value: product.condition.description,
    });
  }

  if (product?.receipt) {
    properties.push({
      '@type': 'PropertyValue',
      name: 'authenticatedBy',
      value: 'Vestiaire Collective',
    });
  }

  const accessories = product?.packagings?.filter((p) => p.value && p.name).map((p) => p.name);
  if (accessories?.length) {
    properties.push({
      '@type': 'PropertyValue',
      name: 'includes',
      value: accessories.join(', '),
    });
  }

  return properties.length > 0 ? { additionalProperty: properties } : {};
};

const getKeywords = (product: Product): string | undefined => {
  const parts = [
    product?.brand?.name,
    product?.category?.localizedName,
    product?.color?.localizedName,
    product?.material?.localizedName,
    product?.condition?.description,
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(', ') : undefined;
};

const getSizeSpecification = (size?: CatalogProduct['size']) => {
  const name = size?.size;
  if (!name) {
    return undefined;
  }

  const sizeSystemLabel = size.standard ?? size.localizedStandard;
  const sizeSystem = sizeSystemLabel
    ? SIZE_SYSTEM_SCHEMA_URL_BY_STANDARD[sizeSystemLabel] ?? sizeSystemLabel
    : undefined;

  return {
    '@type': 'SizeSpecification',
    name,
    ...(sizeSystem && { sizeSystem }),
  };
};

export const createProductSchema = (
  commonFields: ProductSchemaCommonFields,
  options: ProductSchemaOptions = {},
): ProductSchemaType => {
  const productUrl = getProductUrl(commonFields.origin, commonFields.path);
  const priceCents = commonFields.priceCents ?? 0;

  return {
    ...(options.includeContext && { '@context': 'https://schema.org/' }),
    '@type': 'Product',
    ...(productUrl && { '@id': productUrl }),
    name: commonFields.name,
    image: commonFields.image,
    brand: {
      '@type': 'Brand',
      name: commonFields.brandName,
    },
    offers: {
      '@type': 'Offer',
      url: productUrl,
      priceCurrency: commonFields.priceCurrency ?? '',
      price: (priceCents / 100).toString(),
      availability: commonFields.isUnavailable ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
      ...(commonFields.conditionId != null && { itemCondition: getGoogleShoppingCondition(commonFields.conditionId) }),
      ...getPriceSpecification(commonFields.regularPriceCents, priceCents, commonFields.priceCurrency),
      ...options.offerFields,
    },
    ...options.productFields,
  } as ProductSchemaType;
};

export const createProductPageSchema = (
  product: Product,
  seoMetaData?: ContentsPageMetadata,
  origin = '',
): ProductSchemaType => {
  const keywords = getKeywords(product);

  return createProductSchema(
    getCommonProductFields(product, {
      origin,
      image: createImageSchema(product),
      conditionId: product.condition?.id ?? '',
      isUnavailable: Boolean(product.sold),
    }),
    {
      includeContext: true,
      productFields: {
        sku: product.id,
        ...(product?.model?.name && { model: product.model.name }),
        description:
          getDescription(product?.localizedDescription, seoMetaData?.description) ??
          getDescription(product?.description, seoMetaData?.description),
        ...(product?.category && {
          category: product?.category?.localizedName,
        }),
        ...(product?.color && {
          color: product?.color?.localizedName,
        }),
        ...(product?.material && {
          material: product?.material?.localizedName,
        }),
        ...(keywords && { keywords }),
        ...getOptionalMeasurements(product),
        ...getAdditionalProperties(product),
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: 4.5,
          reviewCount: 44885,
        },
      },
      offerFields: {
        priceValidUntil: getPriceValidUntil(),
        ...(!!product?.seller && {
          seller: getSeller(product?.seller, origin),
        }),
      },
    },
  );
};

export const createCatalogProductSchema = (
  product: CatalogProduct,
  searchItem?: ElasticSearchProduct,
  origin = '',
): ProductSchemaType => {
  const productUrl = getProductUrl(origin, product.path);
  const color = product.colors?.all
    ?.map((colorOption) => colorOption.name)
    .filter(Boolean)
    .join(', ');
  const size = getSizeSpecification(product.size);

  return createProductSchema(
    getCommonProductFields(product, {
      origin,
      image: ImagesService.buildUrl(product.pictures?.[0]?.path, { w: 1024, h: 1024 }, true),
      conditionId: searchItem?.condition?.id,
      isUnavailable: Boolean(product.sold || product.shouldBeGone),
    }),
    {
      productFields: {
        productID: product.id,
        ...(productUrl && { url: productUrl }),
        ...(color && { color }),
        ...(searchItem?.description && { description: searchItem.description }),
        ...(size && { size }),
      },
    },
  );
};
